/**
 * A notification in the bell (W11).
 *
 * The bell and the notifications page read the same shape. The words come
 * from the server, already rendered in the reader's language from the same
 * catalogue the email uses (`GET /notifications?locale=`) — so the SPA never
 * composes a sentence about an event, and the bell cannot tell a different
 * story from the inbox.
 *
 * What was here before was the mockup's fixture shape: a title carrying
 * `<strong>` HTML rendered with `v-html`, a pre-formatted "2 hrs ago", and
 * icon classes chosen per row by whoever wrote the fixture. A server-rendered
 * title is plain text now and is printed as text.
 */

/** The four kinds the dispatcher writes (`NOTIFICATION_KINDS`). */
export type NotificationKind =
  | 'match_found'
  | 'deadline_approaching'
  | 'procedure_update'
  | 'system'

export const NOTIFICATION_KINDS: readonly NotificationKind[] = [
  'match_found',
  'deadline_approaching',
  'procedure_update',
  'system',
]

/**
 * The kinds a person can ever receive. The dispatcher sends a client viewer
 * only the case updates the firm shared, and announcements; matches and
 * deadlines go to staff (docs/notifications.md, "Who hears it"). A tab or a
 * switch for any other kind would be one for letters that never come.
 */
export function kindsFor(userKind: string | null | undefined): readonly NotificationKind[] {
  return userKind === 'client_viewer' ? ['procedure_update', 'system'] : NOTIFICATION_KINDS
}

/** The two channels a person can switch per kind (`NOTIFICATION_CHANNELS`). */
export type NotificationChannel = 'in_app' | 'email'

export const NOTIFICATION_CHANNELS: readonly NotificationChannel[] = ['in_app', 'email']

export interface Notification {
  id: number
  kind: NotificationKind | string
  title: string
  body: string
  /** An in-app path to what the notification is about, or null. */
  to: string | null
  unread: boolean
  readAt: string | null
  createdAt: string
  /** Font Awesome classes for the row's icon. */
  icon: string
  /** The icon chip's tone: '' | 'high' | 'gold' | 'blue' (`topbar-popups.css`). */
  tone: '' | 'high' | 'gold' | 'blue'
}

const LOOK: Record<string, { icon: string; tone: Notification['tone'] }> = {
  match_found: { icon: 'fa-solid fa-triangle-exclamation', tone: 'high' },
  deadline_approaching: { icon: 'fa-regular fa-calendar', tone: 'gold' },
  procedure_update: { icon: 'fa-solid fa-scale-balanced', tone: '' },
  system: { icon: 'fa-solid fa-circle-info', tone: 'blue' },
}

export function notificationLook(kind: string): { icon: string; tone: Notification['tone'] } {
  return LOOK[kind] ?? LOOK.system!
}

/**
 * Only an in-app path is followed.
 *
 * The dispatcher writes links like `/oppositions/12` and
 * `/monitoring?risk=high&state=new`. A payload is data from the server, and a
 * link from it is rendered as a `RouterLink`, so anything that is not a plain
 * absolute path inside this app — a full URL, a protocol-relative `//host`, a
 * `javascript:` string — is dropped rather than followed.
 */
export function safeLink(link: unknown): string | null {
  if (typeof link !== 'string') return null
  const value = link.trim()
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return null
  return value
}
